import React, { useState, useRef, useEffect, useCallback } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  TableContainer,
  Paper,
  Box,
  Dialog,
  DialogContent,
  CircularProgress,
  Typography,
  TextField,
} from "@mui/material";
import {
  StyledButton,
  StyledAlert,
  StyledSnackbar,
  StyledAgGridContainer,
  inputStyle,
} from "components/StyledComponents/styledComponents.js";
import { useNavigate } from "react-router-dom";
import useBusinessMonthYear from "components/GlobalHooks/useMonthlyBusiness";
import { writeFile, utils } from "xlsx";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import { monthEndStyles } from "components/Styles/monthEndStyles";
import URLs from "constants/urls";
import Cookie from "js-cookie";
import dayjs from "dayjs";
import { useMaterialUIController } from "context";

const CURRENCY_FIELDS = ["loanAmount", "revenue", "dsaPayoutValue", "companyRetention"];

function buildFooter(rows) {
  if (!rows || rows.length === 0) return null;
  const footer = { Remarks: "Total", footerRow: true };
  CURRENCY_FIELDS.forEach((field) => {
    footer[field] = rows.reduce((s, r) => s + (Number(r[field]) || 0), 0);
  });

  ["RevenuePercentage", "DSAPayoutPercentage", "RetentionPercentage"].forEach((f) => {
    footer[f] = null;
  });
  return footer;
}

export default function MonthlyMISMain() {
  const { monthIdData, setAlert } = useBusinessMonthYear();
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const [monthId, setMonthId] = useState(0);
  const businessCodeRef = useRef(0);
  const gridRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [servicesData, setServicesData] = useState([]);
  const [footerData, setFooterData] = useState([]);

  const MInput = inputStyle();
  const currentMonthStart = dayjs().startOf("month").format("YYYY-MM-DD");
  const currentMonthEnd = dayjs().endOf("month").format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(currentMonthStart);
  const [endDate, setEndDate] = useState(currentMonthEnd);
  const jwt_token = Cookie.get("jwt_token");
  const navigate = useNavigate();

  const showAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };
  const handleCloseAlert = () => setOpen(false);

  const fetchEntriesByMonthId = async () => {
    if (!startDate || !endDate) {
      showAlert("Please select both start and end dates", "error");
      return;
    }
    if (!user) {
      setAlert("Please Login Again", "error");
      return;
    }
    const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
    const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");
    setLoading(true);
    try {
      const response = await fetch(
        `${URLs.misreport}?startDate=${formattedStartDate}&endDate=${formattedEndDate}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt_token}`,
          },
        }
      );
      // if (!response.ok) {
      //   const errorText = await response.text();
      //   console.error("API Error:", errorText);
      //   throw new Error(`API returned status ${response.status}`);
      // }
      // const contentType = response.headers.get("content-type");
      // if (!contentType || !contentType.includes("application/json")) {
      //   throw new Error("Server returned non-JSON response.");
      // }

      if (response.status === 204) {
        setServicesData([]);
        setFooterData([]);
        showAlert("No data found for selected date range", "info");
        return;
      }
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API returned status ${response.status}: ${errorText}`);
      }
      const data = await response.json();
      const result = Array.isArray(data) ? data : [data];
      setServicesData(result);
    } catch (error) {
      showAlert(`Failed to fetch data: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const footer = buildFooter(servicesData);
    setFooterData(footer ? [footer] : []);
  }, [servicesData]);

  useEffect(() => {
    if (monthIdData && monthIdData.length > 0) {
      setMonthId(monthIdData[0].businessMonthID);
      //fetchEntriesByMonthId();
    }
  }, [monthIdData]);

  const onFilterChanged = useCallback(() => {
    if (!gridRef.current?.api) return;
    const visible = [];
    gridRef.current.api.forEachNodeAfterFilter((node) => {
      if (node.data) visible.push(node.data);
    });
    const footer = buildFooter(visible);
    setFooterData(footer ? [footer] : []);
  }, []);

  const handleDownload = () => {
    if (!servicesData.length) return;

    const exportRows = [];
    if (gridRef.current?.api) {
      gridRef.current.api.forEachNodeAfterFilter((node) => {
        if (node.data) exportRows.push(node.data);
      });
    }
    const rows = exportRows.length ? exportRows : servicesData;

    const headers = columns.map((c) => c.headerName);

    const sheetData = rows.map((row) => {
      const obj = {};
      columns.forEach((col) => {
        obj[col.headerName] = row[col.field] ?? "";
      });
      return obj;
    });

    const totalsRow = {};
    columns.forEach((col) => {
      if (CURRENCY_FIELDS.includes(col.field)) {
        totalsRow[col.headerName] = rows.reduce((s, r) => s + (Number(r[col.field]) || 0), 0);
      } else if (col.field === "Remarks") {
        totalsRow[col.headerName] = "Total";
      } else {
        totalsRow[col.headerName] = "";
      }
    });
    sheetData.push(totalsRow);

    const ws = utils.json_to_sheet(sheetData, { header: headers });
    ws["!cols"] = headers.map((h) => ({
      wch: Math.max(h.length, ...sheetData.map((r) => String(r[h] ?? "").length)) + 2,
    }));

    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "MIS Data");
    writeFile(wb, "Monthly_MIS_Data.xlsx");
  };

  const getRowStyle = (params) => {
    if (params.node.rowPinned === "bottom") {
      return {
        background: "#E59400",
        color: "#ffffff",
        fontWeight: "800",
        fontSize: "13px",
        borderTop: "2px solid #c97d00",
      };
    }
    return null;
  };

  const fmtINR = (val) => {
    const n = Number(val);
    return isNaN(n) || n === 0
      ? "₹ 0.00"
      : `₹ ${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const columns = [
    { headerName: "Date", field: "date", filter: true, sortable: true, minWidth: 130, flex: 1 },
    {
      headerName: "Application Number",
      field: "applicationNumber",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
    },
    {
      headerName: "Remarks",
      field: "remarks",
      filter: true,
      sortable: true,
      minWidth: 300,
      flex: 1,
    },
    {
      headerName: "Loan Type",
      field: "loanType",
      filter: true,
      sortable: true,
      minWidth: 120,
      flex: 1,
    },
    { headerName: "Status", field: "status", filter: true, sortable: true, minWidth: 150, flex: 1 },
    {
      headerName: "Customer Name",
      field: "customerName",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1.5,
    },
    {
      headerName: "Loan Amount",
      field: "loanAmount",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    { headerName: "Source", field: "source", filter: true, sortable: true, minWidth: 120, flex: 1 },
    {
      headerName: "Revenue (%)",
      field: "revenuePercentage",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) =>
        p.value !== undefined && p.value !== null ? `${Number(p.value).toFixed(2)}%` : "",
    },
    {
      headerName: "Revenue",
      field: "revenue",
      filter: true,
      sortable: true,
      minWidth: 230,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "DSA Pay-out (%)",
      field: "dsaPayoutPercentage",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) =>
        p.value !== undefined && p.value !== null ? `${Number(p.value).toFixed(2)}%` : "",
    },
    {
      headerName: "DSA Pay-out Value",
      field: "dsaPayoutValue",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Company Retention",
      field: "companyRetention",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Retention (%)",
      field: "retentionPercentage",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) =>
        p.value !== undefined && p.value !== null ? `${Number(p.value).toFixed(2)}%` : "",
    },
    {
      headerName: "DSA Name",
      field: "dsaName",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1,
    },
    {
      headerName: "Managing Director",
      field: "subManager",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
    },
    {
      headerName: "CEO",
      field: "bmChName",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
    },
    {
      headerName: "EMP ID",
      field: "employeeCode",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1,
    },
    {
      headerName: "EMP NAME",
      field: "employeeName",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
    },
    {
      headerName: "Provider Name",
      field: "providerName",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 2,
    },
    {
      headerName: "BANK NAME",
      field: "bankerName",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 2,
    },
  ];

  const triggerApiCall = () => {
    businessCodeRef.current = monthId;
    fetchEntriesByMonthId();
  };

  return (
    <>
      <Dialog open={loading} onClose={() => setLoading(false)}>
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>

      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={handleCloseAlert} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>

      <TableContainer component={Paper} sx={monthEndStyles.tableContainer}>
        <Box sx={monthEndStyles.formContainer}>
          <Box sx={monthEndStyles.formItem}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={{ ...MInput.b, maxWidth: "140px" }}
              InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={{ ...MInput.b, maxWidth: "140px" }}
              InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
            />
            <StyledButton onClick={triggerApiCall}>Get</StyledButton>
          </Box>
          <StyledButton onClick={handleDownload}>
            <DownloadForOfflineIcon fontSize="medium" />
          </StyledButton>
        </Box>

        <StyledAgGridContainer component={Paper} className="ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            rowData={servicesData}
            columnDefs={columns}
            pagination={true}
            paginationPageSize={10}
            pinnedBottomRowData={footerData}
            animateRows={true}
            onFilterChanged={onFilterChanged}
            onSortChanged={onFilterChanged}
            getRowStyle={getRowStyle}
          />
        </StyledAgGridContainer>
      </TableContainer>
    </>
  );
}
